/**
 * MiniHnlMatcher, slide-up popup variant of the full
 * House & Land Matchmaker workspace tool. Same visual treatment as
 * MiniMatcher (anchored sheet on desktop, bottom sheet on mobile) but
 * scoped to 3 quick questions and the top 3 matched packages.
 *
 * Used by the "Shortlist home & land available now" CTA in the
 * Explore Home & Land mega menu. Anonymous users can still get
 * matches inline; deep-clicking a package card sends them through
 * the existing leads/auth flow.
 *
 * Flow:
 *   questions (3 q's) → loading → results (top 3)
 *
 * Backend contract matches the full matchmaker - GET /api/packages
 * with optional ?state= filter, then we score client-side using the
 * same formula as HouseAndLandMatchmakerPage.
 */
import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, MapPin, DollarSign, Bed, Bath, Maximize, ChevronRight, Compass, Home as HomeIcon, ExternalLink } from 'lucide-react';
import axios from 'axios';
import MatchDisclaimer from '../trust/MatchDisclaimer';
import MiniMatcherStateChip from './MiniMatcherStateChip';
import { SiriStyleSpinner } from '../../workspace/components/StandardLoadingScreen';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATES = [
  { v: 'SA', l: 'South Australia' },
  { v: 'VIC', l: 'Victoria' },
  { v: 'NSW', l: 'New South Wales' },
  { v: 'QLD', l: 'Queensland' },
  { v: 'WA', l: 'Western Australia' },
  { v: 'TAS', l: 'Tasmania' },
  { v: 'ACT', l: 'ACT' },
  { v: 'NT', l: 'Northern Territory' },
  { v: 'ALL', l: 'Anywhere in Australia' },
];

// Per-state sub-region picker. We derive the membership client-side
// from each listing's suburb (see ADELAIDE_REGION_MAP / VIC_REGION_MAP
// below) because the DB only carries the coarse `region` field
// ("Adelaide", "Melbourne"...). Suburb lists are taken from the live
// /api/packages dataset on 2026-06-13 and grouped by Adelaide locals'
// mental model (North/South/East/West/Hills) rather than ABS
// statistical boundaries.
const REGIONS_BY_STATE = {
  SA: [
    { v: 'sa-north',  l: 'Northern Adelaide' },
    { v: 'sa-south',  l: 'Southern Adelaide' },
    { v: 'sa-east',   l: 'Eastern Adelaide' },
    { v: 'sa-west',   l: 'Western Adelaide' },
    { v: 'sa-hills',  l: 'Adelaide Hills' },
    { v: 'sa-regional', l: 'Regional SA' },
    { v: 'ANY', l: 'Anywhere in SA' },
  ],
  VIC: [
    { v: 'vic-melb',  l: 'Melbourne (Metro)' },
    { v: 'vic-geelong', l: 'Geelong' },
    { v: 'vic-regional', l: 'Regional VIC' },
    { v: 'ANY', l: 'Anywhere in VIC' },
  ],
  NSW: [
    { v: 'nsw-syd',  l: 'Sydney (Metro)' },
    { v: 'nsw-regional', l: 'Regional NSW' },
    { v: 'ANY', l: 'Anywhere in NSW' },
  ],
  QLD: [
    { v: 'qld-bne',  l: 'Brisbane (Metro)' },
    { v: 'qld-gold', l: 'Gold Coast' },
    { v: 'qld-sun',  l: 'Sunshine Coast' },
    { v: 'qld-regional', l: 'Regional QLD' },
    { v: 'ANY', l: 'Anywhere in QLD' },
  ],
  WA: [
    { v: 'wa-per',  l: 'Perth (Metro)' },
    { v: 'wa-regional', l: 'Regional WA' },
    { v: 'ANY', l: 'Anywhere in WA' },
  ],
  ALL: [{ v: 'ANY', l: 'Anywhere - show me everything' }],
  TAS: [
    { v: 'tas-hobart', l: 'Hobart (Metro)' },
    { v: 'tas-regional', l: 'Regional Tasmania' },
    { v: 'ANY', l: 'Anywhere in Tasmania' },
  ],
  ACT: [
    { v: 'act-canberra', l: 'Canberra' },
    { v: 'ANY', l: 'Anywhere in ACT' },
  ],
  NT: [
    { v: 'nt-darwin', l: 'Darwin' },
    { v: 'nt-regional', l: 'Regional NT' },
    { v: 'ANY', l: 'Anywhere in NT' },
  ],
};

// Adelaide sub-region map. Add new suburbs here as the dataset grows.
const ADELAIDE_REGION_MAP = {
  'sa-north': new Set([
    'angle vale', 'davoren park', 'elizabeth grove', 'elizabeth north',
    'gawler', 'gawler east', 'munno para', 'munno para downs',
    'salisbury downs', 'smithfield', 'para hills', 'ingle farm',
    'modbury north', 'hope valley', 'gulfview heights', 'holden hill',
    'redwood park', 'valley view', 'salisbury', 'pooraka', 'mawson lakes',
    'andrews farm', 'blakeview', 'craigmore', 'one tree hill',
  ]),
  'sa-south': new Set([
    'christie downs', 'reynella', 'seaford', 'aldinga', 'aldinga beach',
    'noarlunga', 'noarlunga downs', 'morphett vale', 'happy valley',
    'flagstaff hill', 'old reynella', 'hackham', 'hackham west',
    'mclaren vale', 'sellicks beach', 'maslin beach', 'port noarlunga',
    "o'sullivan beach", 'christies beach', 'huntfield heights',
  ]),
  'sa-east': new Set([
    'norwood', 'magill', 'kensington', 'kensington gardens', 'rostrevor',
    'paradise', 'campbelltown', 'tranmere', 'glynde', 'firle', 'felixstow',
    'payneham', 'st peters', 'walkerville', 'stepney',
  ]),
  'sa-west': new Set([
    'brooklyn park', 'henley beach', 'grange', 'west lakes', 'flinders park',
    'allenby gardens', 'beverley', 'lockleys', 'fulham', 'fulham gardens',
    'seaton', 'glenelg', 'glenelg north', 'glenelg south', 'glenelg east',
    'somerton park', 'glengowrie', 'morphettville', 'plympton', 'kurralta park',
  ]),
  'sa-hills': new Set([
    'mount barker', 'littlehampton', 'stirling', 'aldgate', 'mount lofty',
    'hahndorf', 'echunga', 'macclesfield', 'callington', 'nairne',
    'bridgewater', 'lobethal', 'woodside',
  ]),
};

const VIC_REGION_MAP = {
  'vic-melb': new Set([
    'mickleham', 'mambourin', 'thornhill park', 'cobblebank',
    'strathtulloh', 'truganina', 'tarneit', 'wyndham vale',
    'point cook', 'werribee', 'rockbank', 'fraser rise',
  ]),
  'vic-geelong': new Set([
    'drysdale', 'ocean grove', 'mount duneed', 'armstrong creek',
    'lara', 'curlewis', 'leopold', 'highton',
  ]),
  'vic-regional': new Set([
    'echuca', 'elliminyt', 'huntly', 'smythes creek', 'winter valley',
    'ballarat', 'bendigo', 'shepparton', 'mildura',
  ]),
};

const _suburbRegion = (state, suburb) => {
  if (!state || !suburb) return null;
  const s = (suburb || '').toLowerCase().trim();
  if (state === 'SA') {
    for (const [region, set] of Object.entries(ADELAIDE_REGION_MAP)) {
      if (set.has(s)) return region;
    }
    return null;
  }
  if (state === 'VIC') {
    for (const [region, set] of Object.entries(VIC_REGION_MAP)) {
      if (set.has(s)) return region;
    }
    return null;
  }
  return null;
};

// Budget brackets - user picks ONE bracket. Each bracket maps to a
// min + max so downstream filtering logic is unchanged. Brackets are
// tighter in the affordable bands and wider above $1m.
const BUDGET_BRACKETS = [
  { id: 'b-under-400',  l: 'Under $400k',     min: 0,       max: 400000 },
  { id: 'b-400-500',    l: '$400k - $500k',   min: 400000,  max: 500000 },
  { id: 'b-500-600',    l: '$500k - $600k',   min: 500000,  max: 600000 },
  { id: 'b-600-700',    l: '$600k - $700k',   min: 600000,  max: 700000 },
  { id: 'b-700-800',    l: '$700k - $800k',   min: 700000,  max: 800000 },
  { id: 'b-800-1000',   l: '$800k - $1m',     min: 800000,  max: 1000000 },
  { id: 'b-1000-1200',  l: '$1m - $1.2m',     min: 1000000, max: 1200000 },
  { id: 'b-1200-1500',  l: '$1.2m - $1.5m',   min: 1200000, max: 1500000 },
  { id: 'b-1500-2000',  l: '$1.5m - $2m',     min: 1500000, max: 2000000 },
  { id: 'b-2000-plus',  l: '$2m+',            min: 2000000, max: 999999999 },
];

const BEDROOMS = [
  { v: '3', l: '3 bedrooms' },
  { v: '4', l: '4 bedrooms' },
  { v: '5+', l: '5+ bedrooms' },
];

const BATHROOMS = [
  { v: '1', l: '1 bathroom' },
  { v: '2', l: '2 bathrooms' },
  { v: '3+', l: '3+ bathrooms' },
  { v: 'ANY', l: 'Any' },
];

const BLOCK_SIZES = [
  { v: 'compact',   l: 'Compact · ≤ 350m²', min: 0,   max: 350 },
  { v: 'standard',  l: 'Standard · 350-550m²', min: 350, max: 550 },
  { v: 'large',     l: 'Large · 550m²+',  min: 550, max: 999999 },
  { v: 'ANY',       l: 'Any size',         min: null, max: null },
];

const fmtPrice = (n) => {
  if (!n || n < 1000) return '$-';
  if (n >= 1000000) return `$${(n / 1000000).toFixed(n % 1000000 === 0 ? 0 : 1)}m`;
  return `$${Math.round(n / 1000)}k`;
};

function MiniHnlMatcher({ open, onClose, sourcePlacement = 'mega-menu-hnl-bottom', sourcePage, initialState = null }) {
  const navigate = useNavigate();
  const [step, setStep] = useState('questions');   // questions | loading | results
  const [state, setState] = useState(null);
  const [region, setRegion] = useState(null);
  const [budgetMin, setBudgetMin] = useState(null);
  const [budgetMax, setBudgetMax] = useState(null);
  const [bedrooms, setBedrooms] = useState(null);
  const [bathrooms, setBathrooms] = useState(null);
  const [blockSize, setBlockSize] = useState(null);
  const [qIdx, setQIdx] = useState(0);             // current question index 0..n
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');

  // Lock body scroll while the sheet is open (matches MiniMatcher
  // behaviour so the page underneath doesn't scroll on iOS).
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Reset state every time the popup re-opens so users always start
  // from a fresh quiz, not a stale answer set. Logged-in users with a
  // known state seed it on open and skip Q1.
  useEffect(() => {
    if (open) {
      setStep('questions');
      setState(initialState || null);
      setRegion(null);
      setBudgetMin(null);
      setBudgetMax(null);
      setBedrooms(null);
      setBathrooms(null);
      setBlockSize(null);
      setQIdx(initialState ? 1 : 0);
      setResults([]);
      setError('');
    }
  }, [open, initialState]);

  const submit = async (overrides = {}) => {
    const _state = overrides.state ?? state;
    const _region = overrides.region ?? region;
    const _budgetMin = overrides.budgetMin ?? budgetMin;
    const _budgetMax = overrides.budgetMax ?? budgetMax;
    const _bedrooms = overrides.bedrooms ?? bedrooms;
    const _bathrooms = overrides.bathrooms ?? bathrooms;
    const _blockSize = overrides.blockSize ?? blockSize;
    if (!(_state && _region && _budgetMax && _bedrooms && _bathrooms && _blockSize)) return;
    setStep('loading');
    setError('');
    try {
      const params = {};
      if (_state && _state !== 'ALL') params.state = _state;
      const minWait = new Promise((r) => setTimeout(r, 1400));
      const call = axios
        .get(`${API}/packages`, { params })
        .then((r) => Array.isArray(r.data) ? r.data : (r.data?.listings || []));
      const [list] = await Promise.all([call, minWait]);

      const bedroomsTarget = _bedrooms === '5+' ? 5 : parseInt(_bedrooms || '0', 10);
      const bathroomsTarget = _bathrooms === '3+' ? 3 : _bathrooms === 'ANY' ? null : parseInt(_bathrooms || '0', 10);
      const blockSizeDef = BLOCK_SIZES.find((b) => b.v === _blockSize) || { min: null, max: null };
      const budgetMinV = _budgetMin || 0;
      const budgetMaxV = _budgetMax || 999999999;

      const scored = (list || [])
        .map((l) => {
          let score = 30; // base
          const beds = l.bedrooms || l.house_land_packages?.[0]?.bedrooms;
          const baths = l.bathrooms || l.house_land_packages?.[0]?.bathrooms;
          const area = l.area_m2 || l.land_area_m2 || 0;

          // bedrooms (strongest signal)
          if (bedroomsTarget > 0 && beds === bedroomsTarget) score += 28;
          else if (bedroomsTarget > 0 && beds === bedroomsTarget + 1) score += 12;
          else if (bedroomsTarget > 0 && beds && beds >= 3) score += 6;

          // bathrooms
          if (bathroomsTarget !== null && baths) {
            if (Math.ceil(baths) === bathroomsTarget) score += 14;
            else if (Math.abs(Math.ceil(baths) - bathroomsTarget) === 1) score += 6;
          } else if (bathroomsTarget === null) {
            score += 4;
          }

          // state exact match
          if (_state !== 'ALL' && l.state === _state) score += 16;

          // region match - uses our suburb→region map for finer granularity
          // than the DB's coarse `region` field. ANY skips the filter.
          if (_region && _region !== 'ANY') {
            const derived = _suburbRegion(_state, l.suburb);
            if (derived === _region) score += 24;
          } else {
            score += 4;
          }

          // block size - only available if the listing has area data.
          if (blockSizeDef && blockSizeDef.min !== null && area > 0) {
            if (area >= blockSizeDef.min && area <= blockSizeDef.max) score += 14;
            else if (Math.abs(area - (blockSizeDef.min + blockSizeDef.max) / 2) < 100) score += 6;
          } else {
            score += 3;
          }

          if (l.titled === 'Titled' || l.title_status === 'titled') score += 6;
          if ((l.images && l.images.length > 0) || l.marketing_images?.length > 0 || l.cover_image || l.image_url) score += 4;
          // Light spread so cards have differentiated match-scores
          // even when several share the same hard signals.
          score += Math.floor(Math.random() * 4);
          return {
            id: l.id || l.listing_id || l.slug,
            name: l.name || l.title || (l.suburb || 'Package'),
            // Build the public-facing address line. Prefer the upstream
            // `address` field; fall back to a stitched lot+street; final
            // fallback is the suburb+state only (kept for packages where
            // no address data is in the feed yet).
            address: (() => {
              const direct = (l.address || '').trim();
              if (direct) return direct;
              const lot   = (l.lot_number || '').toString().trim();
              const street = (l.street || '').toString().trim();
              if (lot && street) return `Lot ${lot}, ${street}, ${l.suburb}`;
              if (lot)           return `Lot ${lot}, ${l.suburb}`;
              if (street)        return `${street}, ${l.suburb}`;
              return [l.suburb, l.state].filter(Boolean).join(', ');
            })(),
            suburb: l.suburb,
            state: l.state,
            from_price: null,   // filled in by the from-prices fetch
            beds,
            baths,
            area_m2: area,
            cover: l.cover_image || l.image_url || (l.marketing_images && l.marketing_images[0]) || (l.images && l.images[0]),
            external_url: l.external_url || l.url || l.listing_url || `/house-and-land-adelaide`,
            match_score: Math.min(99, 50 + score),
          };
        })
        .sort((a, b) => b.match_score - a.match_score)
        .slice(0, 6);

      // Enrich the top 6 with banded "From $X" prices then apply the
      // budget filter precisely (we use the real budget range here for
      // the first time, since prices weren't available on the public
      // catalog object).
      try {
        const ids = scored.map((s) => s.id).filter(Boolean);
        if (ids.length > 0) {
          const fp = await axios.post(`${API}/packages/matcher-from-prices`, { ids });
          const prices = fp.data?.prices || {};
          for (const s of scored) {
            if (prices[s.id]) s.from_price = prices[s.id];
          }
          // Now boost budget-fitting packages so the first card shown
          // is also in-budget. Within-range → +20. Just over (≤10%
          // above max) → +8 with a "stretch" flag. Below min → -10.
          scored.forEach((s) => {
            if (s.from_price == null) return;
            if (s.from_price >= budgetMinV && s.from_price <= budgetMaxV) {
              s.match_score = Math.min(99, s.match_score + 20);
              s.budget_status = 'in_range';
            } else if (s.from_price > budgetMaxV && s.from_price <= budgetMaxV * 1.1) {
              s.match_score = Math.min(99, s.match_score + 8);
              s.budget_status = 'stretch';
            } else if (s.from_price < budgetMinV) {
              s.match_score = Math.max(40, s.match_score - 10);
              s.budget_status = 'under';
            } else {
              s.match_score = Math.max(40, s.match_score - 14);
              s.budget_status = 'over';
            }
          });
          scored.sort((a, b) => b.match_score - a.match_score);
        }
      } catch (_e) { /* non-fatal - cards just won't show From price */ }

      setResults(scored.slice(0, 3));
      setStep('results');
    } catch (err) {
      console.warn('[MiniHnlMatcher] submit failed:', err);
      setError('We could not load packages right now. Browse all packages below, or try again in a moment.');
      // Move to results step so the empty-state card with the
      // Browse-all CTA renders; users never end up stuck on the
      // question page with a bare error bar.
      setResults([]);
      setStep('results');
    }
  };

  const goBrowseAll = () => {
    onClose?.();
    navigate('/house-and-land-adelaide');
  };

  if (!open) return null;

  const overlay = (
    <div
      className="bp-mm-overlay bp-mm-anchor-right"
      role="dialog"
      aria-modal="true"
      data-testid="mini-hnl-matcher"
      data-source-placement={sourcePlacement}
      data-source-page={sourcePage || ''}
    >
      <button type="button" className="bp-mm-scrim" aria-label="Close" onClick={onClose} />
      <div className="bp-mm-panel">
        <header className="bp-mm-header bp-mm-intel-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, padding: '20px 22px 14px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: '#ffffff', margin: '0 0 6px', lineHeight: 1.25 }}>
              Find your house &amp; land package
            </h2>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', margin: '0 0 8px', lineHeight: 1.45 }}>
              7 quick questions. We score live packages against your budget, region and brief.
            </p>
            <span className="bp-mm-intel-tagline" aria-hidden="true">
              <span className="bp-mm-intel-tagline-dot" />
              Powered by BuildPilot Intelligence
            </span>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="mini-hnl-close" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.85)', padding: 4 }}>
            <X size={20} />
          </button>
        </header>

        <div className="bp-mm-body" style={{ padding: '0 22px 18px' }}>
          {/* 2026-02-19: Shared persistent state-chip across all mini
              matchers. Reads the current `state` value; when the user
              picks a state via Q1 (state pill) the chip flips from the
              "we'll ask" prompt to "Matching for: NSW". Clickable
              dropdown lets logged-in users change state mid-brief. */}
          <MiniMatcherStateChip
            state={state && state !== 'ALL' ? state : ''}
            region={(() => {
              if (!region || region === 'ANY' || !state) return '';
              const r = (REGIONS_BY_STATE[state] || []).find((x) => x.v === region);
              return r ? r.l : '';
            })()}
            onChange={(v) => {
              setState(v);
              setRegion(null); // clear region since it's state-dependent
              if (qIdx === 0) setQIdx(1); // advance past Q1 once state is set
            }}
          />
          {step === 'questions' && (() => {
            // One-at-a-time question flow (matches the Builder MiniMatcher
            // pattern). Single-select auto-advances after a brief visual
            // confirmation; the final question fires submit() instead of
            // advancing to a non-existent question.
            const QUESTIONS = [
              { id: 'state', label: 'Which state are you building in?', icon: MapPin, options: STATES, cols: 2, set: setState, current: state },
              {
                id: 'region',
                label: state ? `Which part of ${(STATES.find(s => s.v === state) || {}).l || ''}?` : 'Which region?',
                icon: MapPin,
                options: REGIONS_BY_STATE[state] || REGIONS_BY_STATE.ALL,
                cols: 2,
                set: setRegion,
                current: region,
              },
              {
                id: 'budgetBracket',
                label: 'Budget range',
                icon: DollarSign,
                options: BUDGET_BRACKETS.map((b) => ({ v: b.id, l: b.l })),
                cols: 2,
                set: (id) => {
                  const bracket = BUDGET_BRACKETS.find((b) => b.id === id);
                  if (bracket) {
                    setBudgetMin(bracket.min);
                    setBudgetMax(bracket.max);
                  }
                },
                current: budgetMin != null && budgetMax != null
                  ? (BUDGET_BRACKETS.find((b) => b.min === budgetMin && b.max === budgetMax)?.id || null)
                  : null,
              },
              { id: 'bedrooms', label: 'Bedrooms', icon: Bed, options: BEDROOMS, cols: 3, set: setBedrooms, current: bedrooms },
              { id: 'bathrooms', label: 'Bathrooms', icon: Bath, options: BATHROOMS, cols: 4, set: setBathrooms, current: bathrooms },
              { id: 'blockSize', label: 'Block size', icon: Maximize, options: BLOCK_SIZES, cols: 2, set: setBlockSize, current: blockSize },
            ];
            const q = QUESTIONS[Math.max(0, Math.min(qIdx, QUESTIONS.length - 1))];
            const Icon = q.icon;
            const isLast = qIdx >= QUESTIONS.length - 1;
            const pick = (v) => {
              q.set(v);
              setTimeout(() => {
                if (isLast) {
                  // Pass the latest answer as an override so submit()
                  // doesn't read stale state from the current closure.
                  submit({ [q.id]: v });
                } else {
                  setQIdx(qIdx + 1);
                }
              }, 220);
            };
            return (
              <div className="bp-mm-questions-one-at-a-time">
              <div className="bp-mm-question-single">
                <div className="bp-mm-question-head">
                  <button
                    type="button"
                    onClick={() => qIdx > 0 && setQIdx(qIdx - 1)}
                    disabled={qIdx === 0}
                    aria-label="Back"
                    data-testid="mhnl-back"
                    className="bp-mm-question-back"
                    style={qIdx === 0 ? { color: 'rgba(255,255,255,0.35)', cursor: 'default' } : undefined}
                  >‹ Back</button>
                  <span className="bp-mm-question-counter">
                    {qIdx + 1} of {QUESTIONS.length}
                  </span>
                </div>
                <label className="bp-mm-q-title-lg" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Icon size={14} /> {q.label}
                </label>
                <div className="bp-mm-pick-pills bp-mm-question-pills">
                  {q.options.map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      onClick={() => pick(o.v)}
                      data-testid={`mhnl-${q.id}-${String(o.v).toLowerCase()}`}
                      className={`bp-mm-pick-pill bp-mm-question-pill ${q.current === o.v ? 'is-active' : ''}`}
                    >
                      <span className="bp-mm-pick-pill-label">{o.l}</span>
                    </button>
                  ))}
                </div>
                {error && <p className="bp-mm-error">{error}</p>}
              </div>
              </div>
            );
          })()}

          {step === 'loading' && (
            <div style={{ padding: '40px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <SiriStyleSpinner size={70} />
              <p style={{ fontSize: 13, color: '#ffffff', margin: '16px 0 0', fontWeight: 500 }}>
                Scoring live packages…
              </p>
              <p className="bp-mm-intel-tagline" style={{ marginTop: 8, justifyContent: 'center' }}>
                <span className="bp-mm-intel-tagline-dot" />
                BuildPilot Intelligence is matching you
              </p>
            </div>
          )}

          {step === 'results' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {results.length === 0 ? (
                <div className="bp-mm-concierge-soft" data-testid="mhnl-empty">
                  {error && <p className="bp-mm-error" data-testid="mhnl-empty-error">{error}</p>}
                  <p className="bp-mm-concierge-soft-msg">
                    {error
                      ? 'We hit a snag scoring your brief. Browse the full live catalogue below, or close this and try again in a moment.'
                      : 'No live packages match your brief right now. Browse the full live catalogue or refine your brief to see more options.'}
                  </p>
                  <button
                    type="button"
                    className="bp-mm-primary"
                    onClick={goBrowseAll}
                    data-testid="mhnl-browse-all-empty"
                  >
                    Browse all live H&amp;L packages
                    <ChevronRight size={16} />
                  </button>
                </div>
              ) : (
                <>
                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase', letterSpacing: '0.12em', margin: 0 }}>
                    Top {results.length} matches for you
                  </p>
                  <div
                    data-testid="mhnl-results-liability"
                    style={{
                      background: 'rgba(254, 189, 89, 0.12)',
                      border: '1px solid rgba(254, 189, 89, 0.35)',
                      borderRadius: 10,
                      padding: '10px 12px',
                      color: 'rgba(255,255,255,0.92)',
                      fontSize: 11.5,
                      lineHeight: 1.45,
                    }}
                  >
                    <strong>Indicative match only.</strong> Packages combine live land listings with builder designs. Site conditions, slope, easements, council setbacks and final inclusions vary. <strong>BuildPilot is not the selling agent, not the builder, and is not liable for any land or build decision made on these matches.</strong> Confirm with the builder and a licensed surveyor before signing.
                  </div>
                  {results.map((r) => {
                    const buildInternalHref = () => {
                      const suburbSlug = r.suburb_slug
                        || (r.suburb ? r.suburb.toLowerCase().replace(/\s+/g, '-') : null);
                      const listingSlug = r.listing_slug || r.slug;
                      if (suburbSlug && listingSlug) {
                        return `/house-and-land-adelaide/${suburbSlug}/${listingSlug}`;
                      }
                      return r.external_url || '/house-and-land-packages-adelaide';
                    };
                    const internalHref = buildInternalHref();
                    const isInternal = internalHref.startsWith('/');
                    return (
                    <a
                      key={r.id}
                      href={internalHref}
                      target={isInternal ? '_self' : '_blank'}
                      rel={isInternal ? undefined : 'noopener noreferrer'}
                      onClick={(e) => {
                        if (isInternal) {
                          e.preventDefault();
                          onClose?.();
                          navigate(internalHref);
                        }
                      }}
                      data-testid={`mhnl-result-${r.id}`}
                      className="bp-mm-intel-result-card"
                      style={{
                        display: 'flex',
                        gap: 12,
                        padding: 12,
                        background: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: 14,
                        textDecoration: 'none',
                        color: '#1d1d1f',
                      }}
                    >
                      <div style={{ width: 72, height: 72, borderRadius: 10, overflow: 'hidden', background: '#f3f4f6', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {r.cover ? (
                          <img src={r.cover} alt={r.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                        ) : (
                          <HomeIcon size={26} color="#86868b" />
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, alignItems: 'baseline' }}>
                          <p style={{ fontSize: 14, fontWeight: 600, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} data-testid={`mhnl-result-address-${r.id}`}>
                            {r.address || r.name}
                          </p>
                          <span className="bp-mm-intel-match-chip" style={{ flexShrink: 0 }}>
                            {r.match_score}% match
                          </span>
                        </div>
                        <p style={{ fontSize: 12, color: '#86868b', margin: '2px 0 6px' }}>
                          {[r.suburb, r.state].filter(Boolean).join(', ')}
                          {r.beds ? ` · ${r.beds} bed` : ''}
                        </p>
                        <p style={{ fontSize: 13, fontWeight: 600, color: '#1d1d1f', margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                          {r.from_price ? (
                            <>
                              <span style={{ fontSize: 11, fontWeight: 500, color: '#86868b' }}>From</span>
                              {fmtPrice(r.from_price)}
                            </>
                          ) : (
                            <span style={{ fontSize: 12, fontWeight: 500, color: '#86868b' }}>View pricing</span>
                          )}
                          {!isInternal && <ExternalLink size={12} color="#86868b" />}
                        </p>
                      </div>
                    </a>
                    );
                  })}
                  <button
                    type="button"
                    onClick={goBrowseAll}
                    data-testid="mhnl-browse-all"
                    style={{
                      marginTop: 6,
                      padding: '10px 16px',
                      background: 'rgba(255,255,255,0.10)',
                      color: '#ffffff',
                      border: '1px solid rgba(255,255,255,0.45)',
                      borderRadius: 999,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                      backdropFilter: 'blur(6px)',
                    }}
                  >
                    Browse all live H&amp;L packages
                    <ChevronRight size={14} />
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        <footer className="bp-mm-foot">
          <MatchDisclaimer inline className="!text-[10.5px] mb-1" />
          <a href="/one-on-one-support" className="bp-mm-foot-link">
            Or talk to BuildPilot 1:1 →
          </a>
        </footer>
      </div>
    </div>
  );

  return ReactDOM.createPortal(overlay, document.body);
}

export default MiniHnlMatcher;
